import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { EmptyState, ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get, patch, post, remove } from '../lib/api';
import { defaultGuidelineRules, type GuidelineRules } from '../../shared/schemas';
import { EvidencePanel, ReferencesPanel } from './MaterialsPanels';

type StoredFile = {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  category: string;
  status: string;
  scanState?: 'not_required' | 'pending' | 'scanning' | 'clean' | 'infected' | 'error';
  uploadedAt: string;
};
type RuleSet = {
  id: string;
  name: string;
  version: number;
  status: 'draft' | 'confirmed' | 'active' | 'superseded';
  rules: GuidelineRules;
  guidelineFileId: string | null;
};

export function FilesPage({ projectId }: { projectId: string }) {
  const [tab, setTab] = useState<'guideline' | 'reference' | 'evidence' | 'attachment'>(
    'guideline'
  );
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([]);
  const [rules, setRules] = useState<GuidelineRules>(defaultGuidelineRules);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState('');
  const [fileAction, setFileAction] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [guidelines, attachments, sets] = await Promise.all([
        get<{ files: StoredFile[] }>(`/api/projects/${projectId}/guidelines`),
        get<{ files: StoredFile[] }>(`/api/projects/${projectId}/attachments`),
        get<{ ruleSets: RuleSet[] }>(`/api/projects/${projectId}/rulesets`)
      ]);
      setFiles(tab === 'guideline' ? guidelines.files : attachments.files);
      setRuleSets(sets.ruleSets);
      if (sets.ruleSets[0]) setRules(sets.ruleSets[0].rules);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Berkas tidak dapat dimuat.');
    } finally {
      setLoading(false);
    }
  }, [projectId, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setUploading(true);
    setError('');
    setNotice('');
    const data = new FormData(formElement);
    try {
      const result = await post<{ reused?: boolean }>(
        `/api/projects/${projectId}/${tab === 'guideline' ? 'guidelines' : 'attachments'}`,
        data
      );
      formElement.reset();
      setNotice(
        result.reused
          ? 'Berkas identik sudah tersimpan sebelumnya; salinan baru tidak dibuat.'
          : 'Berkas berhasil disimpan.'
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Upload gagal.');
    } finally {
      setUploading(false);
    }
  }

  async function saveRuleSet() {
    try {
      const currentDraft = ruleSets.find((item) => item.status === 'draft');
      const payload = {
        name: currentDraft?.name ?? `Pedoman Manual v${ruleSets.length + 1}`,
        guidelineFileId: files[0]?.id ?? null,
        rules
      };
      if (currentDraft)
        await patch(`/api/projects/${projectId}/rulesets/${currentDraft.id}`, payload);
      else await post(`/api/projects/${projectId}/rulesets`, payload);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Ruleset gagal disimpan.');
    }
  }

  async function transition(ruleSet: RuleSet, action: 'confirm' | 'activate') {
    try {
      await post(`/api/projects/${projectId}/rulesets/${ruleSet.id}/${action}`);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Status ruleset gagal diubah.');
    }
  }

  async function extractGuideline(fileId: string) {
    setExtracting(fileId);
    setError('');
    try {
      const result = await post<{ extraction: { rules: GuidelineRules } }>(
        `/api/projects/${projectId}/guidelines/${fileId}/extract`
      );
      setRules(result.extraction.rules);
      setNotice('Usulan aturan berhasil dibuat. Tinjau sebelum mengaktifkannya.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Ekstraksi pedoman belum berhasil.');
    } finally {
      setExtracting('');
    }
  }

  async function toggleArchive(file: StoredFile) {
    const action = file.status === 'archived' ? 'restore' : 'archive';
    setFileAction(file.id);
    setError('');
    setNotice('');
    try {
      await post(`/api/projects/${projectId}/attachments/${file.id}/${action}`);
      setNotice(action === 'archive' ? 'Berkas diarsipkan.' : 'Berkas dikembalikan dari arsip.');
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Status berkas belum dapat diubah.');
    } finally {
      setFileAction('');
    }
  }

  async function deleteStoredFile(file: StoredFile) {
    if (!window.confirm(`Hapus berkas “${file.name}”? Metadata audit tetap dipertahankan.`)) return;
    setFileAction(file.id);
    setError('');
    setNotice('');
    try {
      await remove(`/api/projects/${projectId}/attachments/${file.id}`);
      setNotice('Berkas dihapus dari penyimpanan aktif.');
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Berkas belum dapat dihapus.');
    } finally {
      setFileAction('');
    }
  }

  return (
    <div className="content-page files-page">
      <div className="content-heading">
        <div>
          <span className="eyebrow">Bahan dan standar</span>
          <h1>Pedoman & Berkas</h1>
          <p>Simpan file asli dengan aman. Preview turunan tidak pernah menimpa sumber.</p>
        </div>
      </div>
      <div className="segmented">
        <button className={tab === 'guideline' ? 'active' : ''} onClick={() => setTab('guideline')}>
          <SenoIcon name="pedoman" /> Pedoman kampus
        </button>
        <button className={tab === 'reference' ? 'active' : ''} onClick={() => setTab('reference')}>
          <SenoIcon name="referensi" /> Referensi
        </button>
        <button className={tab === 'evidence' ? 'active' : ''} onClick={() => setTab('evidence')}>
          <SenoIcon name="data" /> Data
        </button>
        <button
          className={tab === 'attachment' ? 'active' : ''}
          onClick={() => setTab('attachment')}
        >
          <SenoIcon name="lampiran" /> Berkas proyek
        </button>
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      {notice ? <p className="success-note">{notice}</p> : null}
      {tab === 'reference' ? <ReferencesPanel projectId={projectId} /> : null}
      {tab === 'evidence' ? <EvidencePanel projectId={projectId} /> : null}
      {tab === 'guideline' || tab === 'attachment' ? (
        <>
          <section className="upload-zone">
            <div>
              <SenoIcon name={tab === 'guideline' ? 'pdf' : 'bahan'} />
              <strong>
                {tab === 'guideline' ? 'Upload PDF atau DOCX pedoman' : 'Upload berkas penelitian'}
              </strong>
              <span>
                {tab === 'guideline'
                  ? 'Seno AI membuat usulan aturan. Anda tetap meninjau, mengedit, dan mengaktifkannya.'
                  : 'PDF, DOCX, XLSX, CSV, JPG, PNG, atau WebP — maksimal 3 MB.'}
              </span>
            </div>
            <form onSubmit={upload}>
              <input
                aria-label="Pilih berkas"
                name="file"
                type="file"
                required
                accept={
                  tab === 'guideline' ? '.pdf,.docx' : '.pdf,.docx,.xlsx,.csv,.jpg,.jpeg,.png,.webp'
                }
              />
              {tab === 'attachment' ? (
                <select name="category" aria-label="Kategori">
                  <option value="documentation">Dokumentasi</option>
                  <option value="instrument">Instrumen</option>
                  <option value="raw-data">Data mentah</option>
                  <option value="analysis">Hasil analisis</option>
                  <option value="thesis-source">Skripsi sumber untuk Journal Builder</option>
                  <option value="journal-guideline">Author guideline jurnal</option>
                  <option value="letter">Surat</option>
                  <option value="other">Lainnya</option>
                </select>
              ) : null}
              <button className="button button-primary" disabled={uploading}>
                {uploading ? 'Mengunggah…' : 'Upload berkas'}
              </button>
            </form>
          </section>
          {loading ? (
            <Loading label="Memeriksa penyimpanan…" />
          ) : files.length === 0 ? (
            <EmptyState
              title="Belum ada berkas"
              description="Berkas yang diunggah akan tampil di sini lengkap dengan hash integritas."
            />
          ) : (
            <div className="file-list">
              {files.map((file) => (
                <article className="file-row" key={file.id}>
                  <span className="file-type">
                    <SenoIcon
                      name={
                        file.mimeType.includes('pdf')
                          ? 'pdf'
                          : file.mimeType.includes('word')
                            ? 'docx'
                            : 'bahan'
                      }
                    />
                  </span>
                  <div>
                    <strong>{file.name}</strong>
                    <span>
                      {(file.sizeBytes / 1024).toFixed(1)} KB · SHA-256 {file.sha256.slice(0, 12)}…
                    </span>
                    <span>
                      {file.category || (tab === 'guideline' ? 'guideline' : 'other')} ·{' '}
                      {file.status === 'archived' ? 'Diarsipkan' : 'Aktif'}
                    </span>
                    {file.scanState && file.scanState !== 'not_required' ? (
                      <span className={`scan-state scan-${file.scanState}`}>
                        {file.scanState === 'clean'
                          ? 'Pemeriksaan keamanan selesai'
                          : file.scanState === 'infected'
                            ? 'Berkas diblokir'
                            : 'Menunggu pemeriksaan keamanan'}
                      </span>
                    ) : null}
                  </div>
                  {file.scanState && !['not_required', 'clean'].includes(file.scanState) ? (
                    <button className="button button-secondary" disabled>
                      <SenoIcon name="security" /> Dipindai
                    </button>
                  ) : (
                    <a
                      className="button button-secondary"
                      href={`/api/projects/${projectId}/${tab === 'guideline' ? 'guidelines' : 'attachments'}/${file.id}/download`}
                    >
                      <SenoIcon name="download" /> Unduh
                    </a>
                  )}
                  {tab === 'attachment' ? (
                    <div className="row-actions compact-actions">
                      <button
                        className="button button-ghost"
                        disabled={fileAction === file.id}
                        onClick={() => toggleArchive(file)}
                      >
                        {file.status === 'archived' ? 'Pulihkan' : 'Arsipkan'}
                      </button>
                      <button
                        className="text-button danger"
                        disabled={fileAction === file.id}
                        onClick={() => deleteStoredFile(file)}
                      >
                        Hapus
                      </button>
                    </div>
                  ) : null}
                  {tab === 'guideline' ? (
                    <button
                      className="button button-secondary"
                      disabled={extracting === file.id}
                      onClick={() => extractGuideline(file.id)}
                    >
                      <SenoIcon name="ai" />{' '}
                      {extracting === file.id ? 'Membaca…' : 'Buat usulan aturan'}
                    </button>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </>
      ) : null}
      {tab === 'guideline' ? (
        <section className="rules-editor">
          <div className="section-title">
            <div>
              <span className="eyebrow">Deterministic formatting</span>
              <h2>Ruleset pedoman</h2>
              <p>Aturan ini mengendalikan tampilan dokumen—bukan AI.</p>
            </div>
            <button className="button button-primary" onClick={saveRuleSet}>
              {ruleSets.some((item) => item.status === 'draft')
                ? 'Perbarui draft ruleset'
                : 'Simpan draft ruleset'}
            </button>
          </div>
          <div className="rule-grid">
            <label>
              Font isi
              <input
                value={rules.fontBody}
                onChange={(e) => setRules({ ...rules, fontBody: e.target.value })}
              />
            </label>
            <label>
              Ukuran font
              <input
                type="number"
                min="8"
                max="20"
                value={rules.fontSizePt}
                onChange={(e) => setRules({ ...rules, fontSizePt: Number(e.target.value) })}
              />
            </label>
            <label>
              Spasi baris
              <input
                type="number"
                min="1"
                max="3"
                step="0.1"
                value={rules.lineSpacing}
                onChange={(e) => setRules({ ...rules, lineSpacing: Number(e.target.value) })}
              />
            </label>
            <label>
              Gaya sitasi
              <input
                value={rules.citationStyle}
                onChange={(e) => setRules({ ...rules, citationStyle: e.target.value })}
              />
            </label>
            {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
              <label key={side}>
                Margin{' '}
                {({ top: 'atas', right: 'kanan', bottom: 'bawah', left: 'kiri' } as const)[side]}{' '}
                (cm)
                <input
                  type="number"
                  min="1"
                  max="8"
                  step="0.1"
                  value={rules.marginsCm[side]}
                  onChange={(e) =>
                    setRules({
                      ...rules,
                      marginsCm: { ...rules.marginsCm, [side]: Number(e.target.value) }
                    })
                  }
                />
              </label>
            ))}
            <label>
              Caption tabel
              <select
                value={rules.tableCaption.position}
                onChange={(e) =>
                  setRules({
                    ...rules,
                    tableCaption: { position: e.target.value as 'above' | 'below' }
                  })
                }
              >
                <option value="above">Di atas</option>
                <option value="below">Di bawah</option>
              </select>
            </label>
            <label>
              Caption gambar
              <select
                value={rules.figureCaption.position}
                onChange={(e) =>
                  setRules({
                    ...rules,
                    figureCaption: { position: e.target.value as 'above' | 'below' }
                  })
                }
              >
                <option value="below">Di bawah</option>
                <option value="above">Di atas</option>
              </select>
            </label>
          </div>
          <div className="ruleset-list">
            <h3>Riwayat ruleset</h3>
            {ruleSets.length === 0 ? (
              <p>Belum ada ruleset. Simpan draft pertama setelah memeriksa aturan.</p>
            ) : (
              ruleSets.map((set) => (
                <div className="ruleset-row" key={set.id}>
                  <div>
                    <strong>{set.name}</strong>
                    <span>Versi {set.version}</span>
                  </div>
                  <span className={`badge badge-${set.status}`}>{set.status}</span>
                  <div className="row-actions">
                    {set.status === 'draft' ? (
                      <button
                        className="button button-secondary"
                        onClick={() => transition(set, 'confirm')}
                      >
                        Konfirmasi
                      </button>
                    ) : null}
                    {set.status === 'confirmed' ? (
                      <button
                        className="button button-primary"
                        onClick={() => transition(set, 'activate')}
                      >
                        Aktifkan
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}

